/**
 * Vehicle fitment setups — the bridge between a plate lookup and the search.
 *
 * A car's fitment data is a list of OEM wheel setups. Each setup has a front
 * fitment and a rear fitment. On most cars the two are identical. On a
 * "staggered" car (many Volvo, BMW and Audi models) they differ, for example
 * 235/55R20 front + 255/50R20 rear.
 *
 * Before P25009-364 the plate search flattened every front and rear size into
 * one `has_any` list, so a front-only tyre looked like a match even when
 * nothing fitted the rear. This module turns the raw payload into setups so
 * the search can ask for a COMPLETE set instead: `has_all:<front>,<rear>`,
 * which only matches listings carrying both strings in publicData.combination
 * (see util/listingCombination.js and P25009-365).
 *
 * The plate/fitment backends return three different envelopes, so every
 * reader here is defensive.
 */

/* "235/55R20" / "255/50ZR20" — width / profile / diameter. */
const TIRE_RE = /^(\d+)\/(\d+)\s*Z?R(\d+)/i;

/* "7.5Jx18 ET50.5" → the width/J/x prefix we strip to reach the
 * "<diameter> ET<offset>" form a rim listing is indexed by. */
const RIM_PREFIX_RE = /^\d+(\.\d+)?\s*J?\s*[xX]/;

const clean = value => (typeof value === 'string' ? value.trim() : '');

const stripTrailingZeros = value => value.replace(/\.00\b/g, '');

/* Pick the per-car object regardless of response envelope:
 *   licence-plate API   { result: {...} }  or  { car: {...} }
 *   fitment DB          { data: [{...}] } */
const pickCar = data => {
  if (!data || typeof data !== 'object') return {};
  return data.car || (Array.isArray(data.data) ? data.data[0] : null) || data.result || data || {};
};

/**
 * Rim string as a listing stores it: "7.5Jx18 ET50.5" → "18 ET50.5".
 * Returns '' when the string has no usable diameter/offset.
 */
export const rimToCombination = rim => {
  const trimmed = clean(rim);
  if (!trimmed) return '';
  const stripped = stripTrailingZeros(trimmed.replace(RIM_PREFIX_RE, '').trim());
  // Without an ET the string cannot match a rim listing's combination.
  return /^\d+\s+ET/i.test(stripped) ? stripped : '';
};

/** Diameter as a number from a tyre string ("235/55R20" → 20), else null. */
const tireDiameter = tire => {
  const match = TIRE_RE.exec(clean(tire));
  return match ? Number(match[3]) : null;
};

/** Diameter as a number from a raw rim string ("8.5Jx20 ET40" → 20), else null. */
const rimDiameter = rim => {
  const match = /[xX]\s*(\d+)/.exec(clean(rim));
  return match ? Number(match[1]) : null;
};

/**
 * Cut a tyre string back to the exact form a listing is indexed by.
 *
 * The two sources disagree: the OEM record returns "235/55R19 101T" (with the
 * load index and speed rating) while the fitment rows return a bare
 * "235/55R19". A listing's combination is always "<width>/<profile>R<diameter>"
 * — no load index, and plain R even for a ZR tyre — so anything else can never
 * match, and with `has_all` a single unmatched string means zero results.
 */
const normaliseTire = tire => {
  const match = TIRE_RE.exec(clean(tire));
  return match ? `${match[1]}/${match[2]}R${match[3]}` : '';
};

/* "45" / 45 / "50.5" → a number; a missing or non-numeric offset → null. */
const offsetOf = axle => {
  const raw = axle?.rim_offset;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/**
 * One front/rear fitment pair, with everything the UI and the search need.
 *
 * `isStaggered` is deliberately false for a split-diameter setup: a listing
 * has a single `diameter` field, so a 19" front + 21" rear set cannot be
 * represented and `has_all` would always return nothing (BR-5). Those setups
 * keep individual size options and the UI shows a short explanation.
 */
const toSetup = (front, rear, isOem) => {
  const frontTire = normaliseTire(front?.tire);
  const rearTire = normaliseTire(rear?.tire);
  const frontRimRaw = stripTrailingZeros(clean(front?.rim));
  const rearRimRaw = stripTrailingZeros(clean(rear?.rim));
  const frontRim = rimToCombination(frontRimRaw);
  const rearRim = rimToCombination(rearRimRaw);

  const frontDiameter = tireDiameter(frontTire) ?? rimDiameter(frontRimRaw);
  const rearDiameter = tireDiameter(rearTire) ?? rimDiameter(rearRimRaw);
  const isSplitDiameter =
    frontDiameter != null && rearDiameter != null && frontDiameter !== rearDiameter;

  const isStaggeredTire = !isSplitDiameter && !!frontTire && !!rearTire && frontTire !== rearTire;
  const isStaggeredRim = !isSplitDiameter && !!frontRim && !!rearRim && frontRim !== rearRim;

  return {
    isOem,
    frontTire,
    rearTire,
    frontRimRaw,
    rearRimRaw,
    frontRim,
    rearRim,
    /* The axle's ET as the fitment row gives it (rim_offset), else null — the
     * search page's "Teknisk data" table shows it per axle (P25009-491). */
    frontOffset: offsetOf(front),
    rearOffset: offsetOf(rear),
    frontDiameter,
    rearDiameter,
    isSplitDiameter,
    isStaggeredTire,
    isStaggeredRim,
    isStaggered: isStaggeredTire || isStaggeredRim,
  };
};

const setupKey = setup =>
  [setup.frontTire, setup.rearTire, setup.frontRimRaw, setup.rearRimRaw].join('|');

const isEmptySetup = setup =>
  !setup.frontTire && !setup.rearTire && !setup.frontRimRaw && !setup.rearRimRaw;

/**
 * Normalise a plate / fitment payload into setups.
 *
 * The OEM setup (`original_tires`) comes first when present — it is what the
 * plate search lands on by default, so its position is meaningful, not
 * cosmetic. Duplicate rows are collapsed.
 *
 * @param {Object} data raw lookup payload
 * @returns {Array<Object>} setups, OEM first
 */
export const getCarSetups = data => {
  const car = pickCar(data);
  const rows = Array.isArray(car?.wheels)
    ? car.wheels
    : Array.isArray(car?.tire_info?.tires)
    ? car.tire_info.tires
    : [];

  const rowSetups = rows
    .map(row => toSetup(row?.front, row?.rear, false))
    .filter(setup => !isEmptySetup(setup));

  const original = car?.original_tires;
  const oemSetup = original
    ? toSetup(
        { tire: original.front_tires, rim: original.front_rims },
        { tire: original.rear_tires, rim: original.rear_rims },
        true
      )
    : null;

  let all = rowSetups;
  if (oemSetup && !isEmptySetup(oemSetup)) {
    /* The OEM record is thin: its rim field is often just a width ("8") while
     * the fitment rows carry the full "8Jx19 ET45". When both describe the
     * same tyre fitment, keep the row — it has the rims — and only flag it as
     * the original, rather than adding a rim-less duplicate in front of it. */
    const twin =
      oemSetup.frontTire && oemSetup.rearTire
        ? rowSetups.findIndex(
            setup =>
              setup.frontTire === oemSetup.frontTire && setup.rearTire === oemSetup.rearTire
          )
        : -1;
    all =
      twin >= 0
        ? [{ ...rowSetups[twin], isOem: true }, ...rowSetups.filter((_, i) => i !== twin)]
        : [oemSetup, ...rowSetups];
  }

  const seen = new Set();
  return all.filter(setup => {
    const key = setupKey(setup);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * The setup the plate search lands on: the OEM one when the payload has it,
 * otherwise the first row.
 */
export const getPrimarySetup = data => {
  const setups = Array.isArray(data) ? data : getCarSetups(data);
  return setups.find(setup => setup.isOem) || setups[0] || null;
};

/**
 * True when the plate search should use the set (`has_all`) flow. Based on the
 * PRIMARY setup, because that is the one the search lands on — a car whose OEM
 * fitment is square behaves exactly as it does today even if some optional
 * setup is staggered (BR-4).
 */
export const isStaggeredCar = data => !!getPrimarySetup(data)?.isStaggered;

/** The two search strings for a setup on a given product tab, front first. */
export const setupPair = (setup, adType) => {
  if (!setup) return [];
  return adType === 'rim' ? [setup.frontRim, setup.rearRim] : [setup.frontTire, setup.rearTire];
};

/**
 * Value for the `pub_combination` query param.
 *
 *   complete set   "has_all:235/55R20,255/50R20"  listing must cover BOTH axles
 *   single axle    "has_any:255/50R20"            the deliberate Endast-bak refinement
 *
 * Falls back to `has_any` whenever a pair is not available for this product
 * tab — for example rims that share one "<diameter> ET<offset>" string even
 * though the widths differ, which is common on staggered cars.
 *
 * @param {Object} setup from getCarSetups()
 * @param {'wheel'|'tire'|'rim'} adType product tab
 * @param {'front'|'rear'|null} axle deliberate single-axle refinement
 * @returns {string} param value, or '' when the setup has nothing for this tab
 */
export const combinationParamForSetup = (setup, adType, axle = null) => {
  const [front, rear] = setupPair(setup, adType);

  if (axle === 'front') return front ? `has_any:${front}` : '';
  if (axle === 'rear') return rear ? `has_any:${rear}` : '';

  const pair = [...new Set([front, rear].filter(Boolean))];
  if (pair.length === 0) return '';
  if (pair.length === 1) return `has_any:${pair[0]}`;
  return `has_all:${pair.join(',')}`;
};

/** "235/55R20 fram + 255/50R20 bak", or a single size when not staggered. */
export const setupLabel = (setup, adType) => {
  const [front, rear] = setupPair(setup, adType);
  const pair = [...new Set([front, rear].filter(Boolean))];
  if (pair.length === 0) return '';
  if (pair.length === 1) return pair[0];
  return `${front} fram + ${rear} bak`;
};
